import React, {useEffect, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {Button, Card, Form, Input, Space} from 'antd';
import {LockOutlined, UserOutlined} from '@ant-design/icons';
import {useI18n} from '../../i18n/useI18n';
import {buildLocalizedSitePath} from '../../i18n/siteLocalePath';
import {useHeroCanvas} from '../../hooks/useHeroCanvas';
import AuthShowcasePanel from '../../components/site/AuthShowcasePanel';
import LanguageSwitcher from '../../components/site/LanguageSwitcher';
import {useAuth} from '../../contexts/AuthContext';
import {userService} from '../../services/userService';
import {useSiteContent} from '../site/siteContent';
import styles from './AuthPages.module.css';

const USER_POLL_INTERVAL_MS = 10_000;

interface LoginFormValues {
    username: string;
    password: string;
}

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const {login, isLoading} = useAuth();
    const [form] = Form.useForm<LoginFormValues>();
    const {locale, t} = useI18n();
    const {authLoginHighlights} = useSiteContent();
    const shellRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    useHeroCanvas(shellRef, canvasRef);

    // 已登录用户信息：非空表示已检测到登录态，登录表单应变为跳转按钮
    const [loggedInUser, setLoggedInUser] = useState<string | null>(null);

    // 每 10s 轮询 /api/v1/users/me，若已登录则将登录表单变为跳转按钮
    useEffect(() => {
        let cancelled = false;

        const check = async () => {
            try {
                const user = await userService.getCurrentUser();
                if (cancelled) return;
                if (user && user.username) {
                    setLoggedInUser(user.username);
                } else {
                    // 未登录（接口成功但无用户信息）→ 回退到登录表单
                    setLoggedInUser(null);
                }
            } catch {
                // 未登录或网络异常（含后端停止服务）→ 回退到登录表单
                if (!cancelled) {
                    setLoggedInUser(null);
                }
            }
        };

        check();
        const timer = setInterval(check, USER_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, []);

    const handleSubmit = async (values: LoginFormValues) => {
        const success = await login(values.username, values.password);
        if (success) {
            navigate('/dashboard');
        }
    };

    const handleEnter = () => {
        navigate('/dashboard');
    };

    return (
        <div ref={shellRef} className={styles.shell}>
            <canvas ref={canvasRef} className={styles.heroCanvas}/>
            <div className={styles.shellContent}>
                <div className={styles.langSwitch}>
                    <LanguageSwitcher className={styles.switcher}/>
                </div>
                <AuthShowcasePanel
                    actionLabel={t('auth.login.actionHome')}
                    actionTo="/"
                    highlights={authLoginHighlights}
                    title={t('auth.login.panelTitle')}
                />
                <Card
                    className={styles.card}
                    styles={{
                        body: {padding: 40}
                    }}
                >
                    <div className={styles.header}>
                        <img alt="D-Sense" className={styles.brandLogo} src="/d-sense.png"/>
                        <h2>{t('auth.login.cardTitle')}</h2>
                    </div>

                    {loggedInUser ? (
                        <div style={{marginBottom: 16}}>
                            <div style={{
                                textAlign: 'center',
                                color: '#E2E8F0',
                                fontSize: '14px',
                                marginBottom: 16,
                            }}>
                                {t('auth.login.alreadyLoggedIn')}：{loggedInUser}
                            </div>
                            <Button
                                type="primary"
                                block
                                size="large"
                                onClick={handleEnter}
                            >
                                {t('auth.login.enterSystem')}
                            </Button>
                        </div>
                    ) : (
                        <Form
                            form={form}
                            name="login"
                            onFinish={handleSubmit}
                            layout="vertical"
                            requiredMark={false}
                            size="large"
                        >
                            <Form.Item
                                name="username"
                                rules={[
                                    {required: true, message: t('auth.login.usernameRequired')},
                                ]}
                            >
                                <Input
                                    prefix={<UserOutlined style={{color: '#8892A4'}}/>}
                                    placeholder={t('auth.login.usernamePlaceholder')}
                                    autoComplete="username"
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[
                                    {required: true, message: t('auth.login.passwordRequired')},
                                    {min: 6, message: t('auth.login.passwordMin')},
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined style={{color: '#8892A4'}}/>}
                                    placeholder={t('auth.login.passwordPlaceholder')}
                                    autoComplete="current-password"
                                />
                            </Form.Item>

                            <Form.Item style={{marginBottom: 16}}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    loading={isLoading}
                                >
                                    {t('auth.login.submit')}
                                </Button>
                            </Form.Item>

                        </Form>
                    )}

                    <div style={{textAlign: 'center'}}>
                        <Space size="small">
                        <span style={{color: '#8892A4', fontSize: '14px'}}>
                            {t('auth.login.noAccount')}
                        </span>
                            <Link to={buildLocalizedSitePath('/register', locale)}
                                  style={{fontSize: '14px', color: '#F0B429'}}>
                                {t('auth.login.register')}
                            </Link>
                        </Space>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default LoginPage;
